import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams } from 'react-router-dom';
import Navbar from './components/Navbar.jsx';
import Footer from './components/Footer.jsx';
import CookieConsent from './components/CookieConsent.jsx';
import ScrollSignupPopup from './components/ScrollSignupPopup.jsx';
import SeoHead from './components/SeoHead.jsx';
import { initializeClarity, pageview } from './utils/analytics.js';
import Home from './pages/Home.jsx';
import Features from './pages/Features.jsx';
import Integration from './pages/Integration.jsx';
import Testimonials from './pages/Testimonials.jsx';
import WallOfLove from './pages/WallOfLove.jsx';
import Pricing from './pages/Pricing.jsx';
import Signup from './pages/Signup.jsx';
import Contact from './pages/Contact.jsx';
import AboutUs from './pages/AboutUs.jsx';
import Terms from './pages/Terms.jsx';
import TermsOfUse from './pages/TermsOfUse.jsx';
import TermsAndCondition from './pages/TermsAndCondition.jsx';
import PrivacyPolicy from './pages/PrivacyPolicy.jsx';
import CookiePolicy from './pages/CookiePolicy.jsx';
import ThirdPartyLinks from './pages/ThirdPartyLinks.jsx';
import GDPR from './pages/GDPR.jsx';
import BrandLinkPage from './pages/BrandLinkPage.jsx';
import TestimonialSharePage from './pages/TestimonialSharePage.jsx';
import FAQ from './pages/FAQ.jsx';
import IntegrationDocumentation from './pages/IntegrationDocumentation.jsx';
import Blog from './pages/Blog.jsx';
import BlogPost from './pages/BlogPost.jsx';
import Alternatives from './pages/Alternatives.jsx';
import AlternativePage from './pages/AlternativePage.jsx';
import SEOLandingPage from './pages/SEOLandingPage.jsx';
import './App.css';
import './styles/SharedCTA.css';

import HelpFAQPage from './pages/HelpFAQPage';
import ClientRedirect from './components/ClientRedirect.jsx';
import Industries from './pages/Industries.jsx';
import IndustryDetailPage from './pages/IndustryDetailPage.jsx';
import TestimonialManufacturing from './pages/testimonial-software-for-manufacturing.jsx';
import TestimonialCourier from './pages/testimonial-software-for-courier.jsx';
import TestimonialPharma from './pages/testimonial-software-for-pharma.jsx';
import TestimonialWarehouse from './pages/testimonial-software-for-warehouse.jsx';
import TestimonialFMCG from './pages/testimonial-software-for-fmcg.jsx';
import TestimonialRetail from './pages/testimonial-software-for-retail.jsx';
import TestimonialDistribution from './pages/testimonial-software-for-distribution.jsx';
import Resources from './pages/Resources.jsx';
import Solutions from './pages/Solutions.jsx';
import LocationSolutionPage from './pages/LocationSolutionPage.jsx';
import Locations from './pages/Locations.jsx';
import CityLocation from './pages/CityLocation.jsx';
import { locationSolutionPages } from './utils/locationSolutions.js';

function LegacyBlogRedirect() {
  const { contentType, slug } = useParams();

  return <ClientRedirect to={`/${contentType}/${slug}`} replace />;
}

export function AppLayout() {
  const location = useLocation();
  const isBrandWallPage = location.pathname.startsWith('/w/');
  const isEmbeddedPreview = location.search.includes('preview=1');
  const isPlainPublicPage =
    isEmbeddedPreview ||
    isBrandWallPage ||
    location.pathname.startsWith('/testimonial/') ||
    location.pathname.startsWith('/testimonials/');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    initializeClarity();
    pageview(location.pathname + location.search + location.hash);
  }, [location.pathname, location.search, location.hash]);

  return (
    <div className="App">
      <SeoHead />
      {!isPlainPublicPage && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/features" element={<Features />} />
        <Route path="/features/:slug" element={<Features />} />
        <Route path="/integration" element={<Integration />} />
        <Route path="/integrations" element={<IntegrationDocumentation />} />
        <Route path="/integrations/:platform" element={<IntegrationDocumentation />} />
        <Route path="/integration-documentation" element={<IntegrationDocumentation />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/wall-of-love" element={<WallOfLove />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/kb/:contentType/:slug" element={<LegacyBlogRedirect />} />
        <Route path="/compare/:competitor" element={<SEOLandingPage />} />
        <Route path="/alternatives" element={<Alternatives />} />
        <Route path="/alternatives/:competitor" element={<AlternativePage />} />
        <Route path="/trustpilot-alternatives" element={<AlternativePage />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/locations/:city" element={<CityLocation />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/terms-and-condition" element={<TermsAndCondition />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/third-party-links" element={<ThirdPartyLinks />} />
        <Route path="/gdpr" element={<GDPR />} />
        <Route path="/w/:brandname" element={<BrandLinkPage />} />
        <Route path="/testimonial/:testimonialId" element={<TestimonialSharePage />} />
        <Route path="/testimonials/:testimonialId" element={<TestimonialSharePage />} />
        <Route path="/help/:faqId" element={<HelpFAQPage />} />
        <Route path="/industry-testimonials" element={<Industries />} />
        <Route path="/industry-testimonials/coaches" element={<IndustryDetailPage industry="coaches" />} />
        <Route path="/industry-testimonials/real-estate" element={<IndustryDetailPage industry="realEstate" />} />
        <Route path="/industry-testimonials/healthcare" element={<IndustryDetailPage industry="healthcare" />} />
        <Route path="/industry-testimonials/realtors" element={<IndustryDetailPage industry="realtors" />} />
        <Route path="/industry-testimonials/saas" element={<IndustryDetailPage industry="saas" />} />
        <Route path="/industry-testimonials/agencies" element={<IndustryDetailPage industry="agencies" />} />
        <Route path="/industry-testimonials/consultants" element={<IndustryDetailPage industry="consultants" />} />
        <Route path="/industry-testimonials/ecommerce" element={<IndustryDetailPage industry="ecommerce" />} />
        <Route path="/industry-testimonials/creators" element={<IndustryDetailPage industry="creators" />} />
        <Route path="/industry-testimonials/events" element={<IndustryDetailPage industry="events" />} />
        <Route path="/industry-testimonials/testimonial-software-for-e-commerce" element={<ClientRedirect to="/industry-testimonials/ecommerce" replace />} />
        <Route path="/industry-testimonials/testimonial-software-for-manufacturing" element={<TestimonialManufacturing />} />
        <Route path="/industry-testimonials/testimonial-software-for-courier" element={<TestimonialCourier />} />
        <Route path="/industry-testimonials/testimonial-software-for-pharma" element={<TestimonialPharma />} />
        <Route path="/industry-testimonials/testimonial-software-for-warehouse" element={<TestimonialWarehouse />} />
        <Route path="/industry-testimonials/testimonial-software-for-fmcg" element={<TestimonialFMCG />} />
        <Route path="/industry-testimonials/testimonial-software-for-retail" element={<TestimonialRetail />} />
        <Route path="/industry-testimonials/testimonial-software-for-distribution" element={<TestimonialDistribution />} />
        <Route path="/testimonial-software-for-e-commerce" element={<ClientRedirect to="/industry-testimonials/ecommerce" replace />} />
        <Route path="/testimonial-software-for-manufacturing" element={<ClientRedirect to="/industry-testimonials/testimonial-software-for-manufacturing" replace />} />
        <Route path="/testimonial-software-for-courier" element={<ClientRedirect to="/industry-testimonials/testimonial-software-for-courier" replace />} />
        <Route path="/testimonial-software-for-pharma" element={<ClientRedirect to="/industry-testimonials/testimonial-software-for-pharma" replace />} />
        <Route path="/testimonial-software-for-warehouse" element={<ClientRedirect to="/industry-testimonials/testimonial-software-for-warehouse" replace />} />
        <Route path="/testimonial-software-for-fmcg" element={<ClientRedirect to="/industry-testimonials/testimonial-software-for-fmcg" replace />} />
        <Route path="/testimonial-software-for-retail" element={<ClientRedirect to="/industry-testimonials/testimonial-software-for-retail" replace />} />
        <Route path="/testimonial-software-for-distribution" element={<ClientRedirect to="/industry-testimonials/testimonial-software-for-distribution" replace />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/customer-testimonials" element={<Resources />} />
        <Route path="/testimonial-guide" element={<Resources />} />
        <Route path="/customer-feedback-guide" element={<Resources />} />
        <Route path="/testimonial-marketing" element={<Resources />} />
        <Route path="/testimonial-examples" element={<Resources />} />
        <Route path="/testimonial-templates" element={<Resources />} />
        <Route path="/how-to-collect-testimonials" element={<Resources />} />
        <Route path="/reviews-and-testimonials" element={<Resources />} />
        <Route path="/social-proof-guide" element={<Resources />} />
        <Route path="/video-testimonial-guide" element={<Resources />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/solutions/testimonial-software" element={<Solutions />} />
        <Route path="/solutions/video-testimonial-software" element={<Solutions />} />
        <Route path="/solutions/testimonial-collection-software" element={<Solutions />} />
        <Route path="/solutions/ugc-testimonial-software" element={<Solutions />} />
        <Route path="/solutions/social-proof-software" element={<Solutions />} />
        {locationSolutionPages.map((page) => (
          <Route key={page.slug} path={`/solutions/${page.slug}`} element={<LocationSolutionPage page={page} />} />
        ))}
        {/* Backwards-compatible redirects from older top-level routes */}
        <Route path="/testimonial-software" element={<ClientRedirect to="/solutions/testimonial-software" replace />} />
        <Route path="/video-testimonial-software" element={<ClientRedirect to="/solutions/video-testimonial-software" replace />} />
        <Route path="/testimonial-collection-software" element={<ClientRedirect to="/solutions/testimonial-collection-software" replace />} />
        <Route path="/ugc-testimonial-software" element={<ClientRedirect to="/solutions/ugc-testimonial-software" replace />} />
        <Route path="/social-proof-software" element={<ClientRedirect to="/solutions/social-proof-software" replace />} />
        <Route path="/:contentType" element={<Blog />} />
        <Route path="/:contentType/:slug" element={<BlogPost />} />
      </Routes>
      {!isPlainPublicPage && <Footer />}
      {!isPlainPublicPage && <ScrollSignupPopup />}
      {!isPlainPublicPage && <CookieConsent />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}

export default App;
